import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui';
import { CopyBlock, dracula } from '../../components/CodeBlock';

const BASE_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) ||
  'https://api.walytic.com';

const CURL_PING = `curl ${BASE_URL}/api/sessions \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

const Overview = () => {
  return (
    <div className="p-2 md:p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-2">Walytic API</h1>
      <p className="text-muted-foreground mb-6">
        Build WhatsApp into your product. Connect sessions, send messages,
        manage contacts, run campaigns, and react to events in real time — all
        through a simple REST API with <code className="font-mono">x-api-key</code>{' '}
        and <code className="font-mono">X-Workspace-Id</code> headers.
      </p>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">What you can build</h2>
        <ul className="list-disc list-inside text-sm space-y-1 text-gray-700">
          <li>Connect one or more WhatsApp numbers as <strong>sessions</strong></li>
          <li>Send text, media, documents, and templated messages to individuals or groups</li>
          <li>Run <strong>bulk campaigns</strong> from Excel uploads or sequenced outreach</li>
          <li>Verify WhatsApp numbers in bulk before you message them</li>
          <li>Sync contacts / CRM and receive inbound messages via <strong>webhooks</strong></li>
          <li>Automate replies with <strong>AutoFlows</strong> — a visual flow builder</li>
        </ul>
      </Card>

      <Card className="p-4 my-4 bg-muted">
        <h2 className="font-semibold mb-2">Base URL</h2>
        <p className="font-mono text-sm mb-3">{BASE_URL}</p>
        <h3 className="font-semibold mb-2">Ping your account</h3>
        <CopyBlock language="bash" text={CURL_PING} theme={dracula} wrapLines />
        <p className="text-xs text-gray-500 mt-2">
          Get your API key from{' '}
          <Link to="/settings/api-keys" className="text-sky-600 hover:underline">
            Settings → API Keys
          </Link>
          . The same screen shows the active workspace client ID.
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">Workspace tenancy</h2>
        <p className="text-sm text-gray-700">
          Every API call is scoped to one workspace. A user can belong to many
          workspaces, and an API key can only be used with the workspace that
          owns it. Send <code className="font-mono">X-Workspace-Id</code> on
          every request. <code className="font-mono">X-Client-Id</code> is
          accepted as an alias for integrations that call tenants clients.
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">How <code className="font-mono">sessionId</code> works</h2>
        <p className="text-sm text-gray-700 mb-2">
          A <strong>session</strong> represents one connected WhatsApp number. The{' '}
          <code className="font-mono">sessionId</code> is simply that WhatsApp
          phone number in <strong>E.164 digits with no leading <code>+</code></strong>.
        </p>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
          <li><code className="font-mono">+91 98765 43210</code> → <code className="font-mono">919876543210</code></li>
          <li><code className="font-mono">+1 (415) 555-0100</code> → <code className="font-mono">14155550100</code></li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">
          See <Link to="sessions/scan-qr" className="text-sky-600 hover:underline">Scan QR</Link>{' '}
          to bring a new number online.
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">Rate limits</h2>
        <p className="text-sm text-gray-700">
          Requests are rate-limited per API key and per plan. If you hit the
          limit, the API responds with <code className="font-mono">429 Too Many Requests</code>{' '}
          — back off and retry. The exact ceiling depends on your subscription;
          check your current plan under{' '}
          <Link to="/settings/billing" className="text-sky-600 hover:underline">
            Settings → Billing
          </Link>
          .
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">Start here</h2>
        <ul className="list-disc list-inside text-sm space-y-1">
          <li>
            <Link to="authentication" className="text-sky-600 hover:underline">
              Authentication
            </Link>{' '}
            — how to send your API key
          </li>
          <li>
            <Link to="try-it" className="text-sky-600 hover:underline">
              Try It (Sandbox)
            </Link>{' '}
            — fire real requests against your account
          </li>
          <li>
            <Link to="sessions/sessions-crud" className="text-sky-600 hover:underline">
              Sessions API
            </Link>{' '}
            — create, list, reconnect, delete
          </li>
          <li>
            <Link to="messaging/send-message" className="text-sky-600 hover:underline">
              Messaging API
            </Link>{' '}
            — send your first message
          </li>
          <li>
            <Link to="webhooks/setup" className="text-sky-600 hover:underline">
              Webhooks
            </Link>{' '}
            — receive inbound events
          </li>
          <li>
            <Link to="api-reference" className="text-sky-600 hover:underline">
              Full API Reference
            </Link>
          </li>
        </ul>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">What&apos;s new</h2>
        <p className="text-sm text-gray-700">
          See the latest API additions, breaking changes, and fixes in the{' '}
          <Link to="/admin/changelog" className="text-sky-600 hover:underline">
            changelog
          </Link>
          .
        </p>
      </Card>
    </div>
  );
};

export default Overview;
